// src/pages/About.tsx
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

const About: React.FC = () => {
  const [expandedBios, setExpandedBios] = useState<{ [key: string]: boolean }>({});

  const toggleBio = (name: string) => {
    setExpandedBios(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const truncateBio = (bio: string, maxLength: number = 150) => {
    if (bio.length <= maxLength) return bio;
    return bio.substring(0, maxLength) + '...';
  };

  const boardMembers = [
    {
      name: "Gloria Loriya",
      role: "Founder & Executive Director",
      bio: "Gloria Cherop Loriya is the Founder and Executive Director of Impact Generation Africa (IGA). Gloria holds an MSc in Food Security from the University of Edinburgh in the Uk where she is a Mastercard Foundation Scholar. She also holds a bsc in human nutrition and dietetics .Her interdisciplinary background bridges nutrition, climate adaptation, and Indigenous knowledge systems, enabling her to design practical, community-driven approaches to food security.Born and raised in West Pokot County, Gloria experienced firsthand the challenges facing pastoralist families ,from chronic droughts to malnutrition and limited livelihood options for women. These realities inspired her to establish IGA for locally led action, empowering Indigenous communities, especially women and youth, to lead the restoration of their lands and protecting the environment.Before founding IGA, Gloria worked with leading development organizations, including Amref Health Africa, World Vision Kenya, and UNICEF, contributing to nutrition-sensitive interventions and community-based programs addressing child malnutrition, maternal health, and sustainable livelihoods. These experiences deepened her understanding of how climate change, poverty, and nutrition are interconnected and how empowering local communities can break that cycle.Her distinguished MSc research, 'Exploring Carbon Credit Awareness and Food Security Initiatives: Indigenous Women Participation in West Pokot', underscores her commitment to linking carbon markets and climate finance to grassroots food security and gender equality.Regionally, Gloria has served as the Indigenous Perspectives Technical Expert under Oxfam Consults ,Alinea International's Climate Action Africa program and global affairs canada on various projects .She is also part of the African group of negotiators and expert support on climate action At the global level, she is a Project Ambassador in the UK, co-led  the creation of the UK Youth Food and Farming Chapter under the World Food Forum (WFF) model .This is  a youth-led platform connecting global dialogues to local realities in food, climate, and land governance.Gloria's leadership is anchored in the belief that Food systems transformation must begin with those most affected by climate change ,Indigenous communities living in arid and semi-arid areas. When we invest in their knowledge, leadership, and land, we invest in the planet's future.Through her work, Gloria continues to amplify Indigenous voices, nurture climate-resilient livelihoods, and inspire a new generation of leaders who see climate justice not as an abstract idea but as a lived commitment to dignity, equity, and regeneration.",
      image: "/images/ceo.jpeg"
    },
    {
      name: "Ruth Wanjiru Wachera",
      role: "Board Member",
      bio: "Ruth Wanjiru Wachera is a distinguished member of our board of directors, offering comprehensive expertise in community development and environmental studies. She holds a Master's degree in this field and has received prestigious accolades, including the Ombudsman Award (2023) and the Shujaa Environment Conservation Award (2024), which underscore her significant contributions to environmental stewardship and social equity.",
      image: "/images/director.jpeg"
    },
    {
      name: "Valentine Jepkoech Mibei",
      role: "Board Member",
      bio: "Valentine Jepkoech Mibei is a research scientist by profession with a degree in Bsc medical biochemistry from Jomo Kenyatta University of Agriculture and Technology with Kenya medical Research institute (KEMRI) as research scientist .",
      image: "/images/director-1.jpeg"
    },
    {
      name: "Asbel  Kemboi",
      role: "Board Member",
      bio: "Asbel  Kemboi is a passionate environmentalist and community leader . He serves as the Executive Director of the Network for Community Development (NCD), leading youth and community initiatives on climate action, environmental education and sustainable development. He actively works with schools and local organizations across North Rift region, empowering young people to take practical action for a better environment. He also serves as the Club Chairman of Nandi Youth Football Club, among other leadership and advisory roles at both community and national levels, inspiring grassroots action for a greener and more empowered future.",
      image: "/images/board-member.jpeg"
    }
  ];

  const staffMembers = [
    {
      name: "Divina Cherop",
      role: "Project Manager",
      bio: "Focused on engaging youth in climate action and sustainable practices.",
      image: "/images/project-manager.jpeg"
    },
    {
      name: "Silas Lolem",
      role: "Program Officer",
      bio: "Expert programs implementation and community outreach.",
      image: "/images/po.jpeg"
    },
    {
      name: "Anthony Mnangat",
      role: "Logistics Officer",
      bio: "Skilled in managing logistics and supply chain operations.",
      image: "/images/lo.jpeg"
    }
  ];

  const values = [
    {
      icon: "🌱",
      title: "Sustainability",
      description: "We implement solutions that are environmentally, economically, and socially sustainable for generations to come."
    },
    {
      icon: "🤝",
      title: "Community-Led",
      description: "Our programs are designed and implemented in partnership with local communities, ensuring cultural relevance and ownership."
    },
    {
      icon: "🔍",
      title: "Transparency",
      description: "We maintain open communication about our operations, finances, and impact with all stakeholders."
    },
    {
      icon: "💡",
      title: "Innovation",
      description: "We embrace innovative approaches and technologies to solve complex development challenges."
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Us - Impact Generation Africa</title>
        <meta 
          name="description" 
          content="Learn about Impact Generation Africa's mission, vision, team, and our commitment to sustainable development in African communities." 
        />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="py-5 bg-success text-white"
        style={{ marginTop: '76px' }}
      >
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <motion.h1 
                className="display-5 fw-bold mb-4"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                About Impact Generation Africa
              </motion.h1>
              <motion.p 
                className="lead mb-0"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Building sustainable futures through community empowerment and innovative solutions
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <motion.h2 
                className="display-6 fw-bold text-success mb-4"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                Our Story
              </motion.h2>
              <motion.p 
                className="text-muted mb-4"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Founded in 2022, Impact Generation Africa emerged from a simple yet powerful vision: 
                to create sustainable solutions to food insecurity and climate challenges facing African communities.
              </motion.p>
              <motion.p 
                className="text-muted mb-4"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
              >
                What started as a small initiative has grown into a comprehensive organization 
                implementing programs across multiple countries, touching thousands of lives, 
                and creating lasting positive change.
              </motion.p>
            </div>
            <div className="col-lg-6">
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <img 
                  src="/images/about-story.jpeg" 
                  alt="Our story" 
                  className="img-fluid rounded shadow"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-md-6 mb-4">
              <motion.div 
                className="card border-0 shadow-sm h-100"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="card-body text-center p-4">
                  <div className="text-success mb-3" style={{ fontSize: '3rem' }}>🎯</div>
                  <h4 className="card-title text-success">Our Mission</h4>
                  <p className="card-text text-muted">
                    We strive to protect, restore, and enhance the natural environment 
                    sustainable practices. Through traditional ecological knowledge, 
                    we promote good health, environmental integrity, and economic prosperity, 
                    ensuring that our land remains fertile and productive for future generations.
                  </p>
                </div>
              </motion.div>
            </div>
            <div className="col-md-6 mb-4">
              <motion.div 
                className="card border-0 shadow-sm h-100"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="card-body text-center p-4">
                  <div className="text-success mb-3" style={{ fontSize: '3rem' }}>👁️</div>
                  <h4 className="card-title text-success">Our Vision</h4>
                  <p className="card-text text-muted">
                    To be a leading force in creating climate-resilient and food-secure 
                    communities, guided by the values of our ancestors. We envision a future 
                    where modern innovation work hand in hand to restore balance to our 
                    environment and empower communities to thrive sustainably.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-5">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-12">
              <motion.h2 
                className="display-6 fw-bold text-success mb-3"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                Our Core Values
              </motion.h2>
            </div>
          </div>
          <div className="row">
            {values.map((value, index) => (
              <motion.div 
                key={index}
                className="col-md-6 col-lg-3 mb-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card border-0 shadow-sm h-100 text-center">
                  <div className="card-body p-4">
                    <div className="display-4 mb-3">{value.icon}</div>
                    <h5 className="card-title text-success">{value.title}</h5>
                    <p className="card-text text-muted small">{value.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Board Members Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-12">
              <motion.h2 
                className="display-6 fw-bold text-success mb-3"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                Our Board
              </motion.h2>
              <motion.p 
                className="lead text-muted"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Leadership committed to driving our vision forward
              </motion.p>
            </div>
          </div>

          <div className="row">
            {boardMembers.map((member, index) => (
              <motion.div 
                key={index}
                className="col-md-6 col-lg-3 mb-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card border-0 shadow-sm h-100 text-center">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="card-img-top rounded-circle mx-auto mt-4"
                    style={{
                      width: "150px",
                      height: "150px",
                      objectFit: "cover",
                      border: "4px solid #198754",
                    }}
                  />
                  <div className="card-body">
                    <h5 className="card-title text-success">{member.name}</h5>
                    <h6 className="card-subtitle mb-2 text-muted">{member.role}</h6>
                    <p className="card-text small text-muted">
                      {expandedBios[member.name] ? member.bio : truncateBio(member.bio)}
                    </p>
                    {member.bio.length > 150 && (
                      <button
                        className="btn btn-link text-success p-0 text-decoration-none"
                        onClick={() => toggleBio(member.name)}
                      >
                        {expandedBios[member.name] ? 'Read Less' : 'Read More'}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Staff Members Section */}
      <section className="py-5">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-12">
              <motion.h2 
                className="display-6 fw-bold text-success mb-3"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                Our Staff
              </motion.h2>
              <motion.p 
                className="lead text-muted"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Dedicated professionals implementing our programs on the ground
              </motion.p>
            </div>
          </div>

          <div className="row justify-content-center">
            {staffMembers.map((member, index) => (
              <motion.div 
                key={index}
                className="col-md-6 col-lg-4 mb-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card border-0 shadow-sm h-100 text-center">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="card-img-top rounded-circle mx-auto mt-4"
                    style={{
                      width: "150px",
                      height: "150px",
                      objectFit: "cover",
                      border: "4px solid #198754",
                    }}
                  />
                  <div className="card-body">
                    <h5 className="card-title text-success">{member.name}</h5>
                    <h6 className="card-subtitle mb-2 text-muted">{member.role}</h6>
                    <p className="card-text small text-muted">
                      {expandedBios[member.name] ? member.bio : truncateBio(member.bio)}
                    </p>
                    {member.bio.length > 150 && (
                      <button
                        className="btn btn-link text-success p-0 text-decoration-none"
                        onClick={() => toggleBio(member.name)}
                      >
                        {expandedBios[member.name] ? 'Read Less' : 'Read More'}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default About;